import {
  DeleteObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  S3Client,
} from '@aws-sdk/client-s3'
import { getSignedUrl } from '@aws-sdk/s3-request-presigner'

function requireEnv(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(`Missing Cloudflare R2 configuration: ${name} is required.`)
  }
  return value
}

function getR2Env() {
  const get = (key: string) => import.meta.env[key]

  return {
    endpoint: requireEnv('VITE_CF_R2_ENDPOINT', get('VITE_CF_R2_ENDPOINT')),
    bucketName: requireEnv('VITE_CF_R2_BUCKET_NAME', get('VITE_CF_R2_BUCKET_NAME')),
    accessKeyId: requireEnv('VITE_CF_R2_ACCESS_KEY_ID', get('VITE_CF_R2_ACCESS_KEY_ID')),
    secretAccessKey: requireEnv(
      'VITE_CF_R2_SECRET_ACCESS_KEY',
      get('VITE_CF_R2_SECRET_ACCESS_KEY'),
    ),
  }
}

function createR2Client(): S3Client {
  const { endpoint, accessKeyId, secretAccessKey } = getR2Env()
  return new S3Client({
    region: 'auto',
    endpoint,
    credentials: { accessKeyId, secretAccessKey },
  })
}

function getBucketName(): string {
  return getR2Env().bucketName
}

export type StoragePayload = Blob | string | Uint8Array | ArrayBuffer | ReadableStream<Uint8Array>

export interface StorageFileSummary {
  key: string
  size: number
  lastModified?: Date
}

export const STORAGE_FOLDERS = {
  USER_AVATARS: 'user_avatars',
  PROPERTY_PHOTOS: 'property_photos',
  SCREEN_PHOTOS: 'screen_photos',
  COMPANY_LOGOS: 'company_logos',
  RECEIPTS: 'receipts',
  TEST: 'test',
} as const

export type StorageFolder = (typeof STORAGE_FOLDERS)[keyof typeof STORAGE_FOLDERS]

export function resolveStorageKey(folder: StorageFolder, filename: string): string {
  const normalizedFolder = folder.replace(/\/+$/g, '')
  const normalizedFilename = filename.replace(/^\/+/, '')
  return `${normalizedFolder}/${normalizedFilename}`
}

export function getStorageBucket(): string {
  return getBucketName()
}

export function getStorageBaseUrl(): string {
  const { endpoint, bucketName } = getR2Env()
  return `${endpoint}/${bucketName}`
}

export function encodeStorageKey(key: string): string {
  return key
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')
}

export function getStorageUrl(key: string): string {
  return `${getStorageBaseUrl()}/${encodeStorageKey(key)}`
}

export async function getDisplayUrl(key: string | null | undefined): Promise<string> {
  if (!key) return ''
  if (key.startsWith('http://') || key.startsWith('https://') || key.startsWith('data:')) return key
  try {
    return await getPresignedStorageUrl(key, 86400)
  } catch {
    return getStorageUrl(key)
  }
}

export function getStorageFileUrl(folder: StorageFolder, filename: string): string {
  return getStorageUrl(resolveStorageKey(folder, filename))
}

async function toUint8Array(body: StoragePayload): Promise<Uint8Array> {
  if (body instanceof Blob) return new Uint8Array(await body.arrayBuffer())
  if (typeof body === 'string') return new TextEncoder().encode(body)
  if (body instanceof ArrayBuffer) return new Uint8Array(body)
  if (body instanceof Uint8Array) return body
  const reader = body.getReader()
  const chunks: Uint8Array[] = []
  let result = await reader.read()
  while (!result.done) {
    chunks.push(result.value)
    result = await reader.read()
  }
  const total = chunks.reduce((sum, c) => sum + c.length, 0)
  const out = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    out.set(chunk, offset)
    offset += chunk.length
  }
  return out
}

export async function uploadStorageObject(
  key: string,
  body: StoragePayload,
  contentType = 'application/octet-stream',
): Promise<{ key: string; url: string }> {
  const client = createR2Client()
  const bodyBytes = await toUint8Array(body)
  await client.send(
    new PutObjectCommand({
      Bucket: getBucketName(),
      Key: key,
      Body: bodyBytes,
      ContentType: contentType,
    }),
  )
  return { key, url: getStorageUrl(key) }
}

export async function uploadStorageFile(
  folder: StorageFolder,
  filename: string,
  body: StoragePayload,
  contentType = 'application/octet-stream',
): Promise<{ key: string; url: string }> {
  return uploadStorageObject(resolveStorageKey(folder, filename), body, contentType)
}

export async function deleteStorageObject(key: string): Promise<void> {
  const client = createR2Client()
  await client.send(
    new DeleteObjectCommand({
      Bucket: getBucketName(),
      Key: key,
    }),
  )
}

export async function deleteStorageFile(folder: StorageFolder, filename: string): Promise<void> {
  return deleteStorageObject(resolveStorageKey(folder, filename))
}

export async function listStorageObjects(
  prefix = '',
  maxKeys = 100,
): Promise<StorageFileSummary[]> {
  const client = createR2Client()
  const response = await client.send(
    new ListObjectsV2Command({
      Bucket: getBucketName(),
      Prefix: prefix,
      MaxKeys: maxKeys,
    }),
  )
  return (
    response.Contents?.map((item) => ({
      key: item.Key ?? '',
      size: item.Size ?? 0,
      lastModified: item.LastModified ? new Date(item.LastModified) : undefined,
    })) ?? []
  )
}

export async function listStorageFolderObjects(
  folder: StorageFolder,
  maxKeys = 100,
): Promise<StorageFileSummary[]> {
  return listStorageObjects(`${folder}/`, maxKeys)
}

export async function getPresignedStorageUrl(key: string, expiresIn = 900): Promise<string> {
  const client = createR2Client()
  return getSignedUrl(client, new GetObjectCommand({ Bucket: getBucketName(), Key: key }), {
    expiresIn,
  })
}
